import React from "react";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { Box } from "@material-ui/core";
import { useQuery } from "@apollo/react-hooks";

import coinIcon from "assets/coin-icon.svg";
import dayStreakIcon from "assets/day-streak-icon.svg";
import { appWidth, color, fontSize, spacingDefaults } from "style/constants";
import { StyledGrid } from "components/simple";
import { StyledCoin } from "components/pages/DashboardV2/TaskList/styled";
import UserStreakDrawer from "components/simple/UserStreakDrawer";
import PulsePill from "components/simple/PulsePill";
import { GetLatestPulseScore, GetPulseEnabled } from "graphql/pulse/pulse.gql";
import routesConfig from "utils/routesConfig";

import { getUser } from "state";

interface SubHeaderProps {
  dayStreak: number;
  userCoinAmount: number;
  streakDrawerOpen: boolean;
  setStreakDrawer: (isOpen: boolean) => void;
}

export const SUB_HEADER_HEIGHT = 35;

const SubHeader: React.FC<SubHeaderProps> = ({
  dayStreak,
  userCoinAmount,
  streakDrawerOpen,
  setStreakDrawer
}) => {
  const history = useHistory();
  const location = history.location.pathname;

  const user = useSelector(getUser);
  const sammonsUser = user?.organization?.name === "sammons";
  const goldmanUser = user?.organization?.name === "goldman-sachs";
  const pittCrewUser = user?.organization?.name === "pitt-crew";
  const bbsPsychPilotUser = user?.organization?.name === "bbs psych pilot";

  const { data: pulseEnabledData } = useQuery(GetPulseEnabled);
  const isPulseEnabled =
    pulseEnabledData?.me?.organization?.pulseEnabled ?? false;

  const { data: pulseScoreData } = useQuery(GetLatestPulseScore, {
    skip: !isPulseEnabled
  });
  const pulseScore = pulseScoreData?.latestPulseScore?.pulseIndex ?? 0;
  const hasTakenPulse = pulseScoreData?.latestPulseScore != null;

  const pathsToShow = [
    "/dashboard",
    "/profile",
    "/resources",
    ...(!(sammonsUser || goldmanUser || pittCrewUser || bbsPsychPilotUser)
      ? ["/index"]
      : []),
    "/training",
    "/faq",
    "/myjourney"
  ];

  const shouldShow = pathsToShow.some(
    path => path === location || location.includes(path + "/")
  );

  function openStats() {
    setStreakDrawer(false);
    history.push("/stats");
  }

  return (
    <>
      {shouldShow && (
        <StyledGrid
          item
          height={`${SUB_HEADER_HEIGHT}px`}
          boxShadow="0px 2px 12px rgb(0 0 0 / 10%)"
          backgroundColor={color.WHITE}
        >
          <StyledGrid
            container
            height="100%"
            alignItems="center"
            margin="0 auto"
            maxWidth={appWidth.max}
            padding={`0 ${spacingDefaults.normal}`}
            justifyContent="space-between"
          >
            <UserStreakDrawer
              inputDate={new Date()}
              drawerOpen={streakDrawerOpen}
              setStreakDrawer={setStreakDrawer}
            />
            <StyledGrid
              item
              onClick={() => setStreakDrawer(true)}
              style={{ cursor: "pointer" }}
            >
              <StyledGrid container>
                <img
                  src={dayStreakIcon}
                  style={{ marginRight: spacingDefaults.xsmall }}
                />
                <Box fontSize={fontSize.small}>{`${dayStreak} Day Streak`}</Box>
              </StyledGrid>
            </StyledGrid>
            <StyledGrid
              container
              item
              style={{ gap: spacingDefaults.xsmall, width: "auto" }}
              alignItems="center"
            >
              <PulsePill
                isActive={hasTakenPulse}
                isPulseEnabled={isPulseEnabled}
                onPress={() => history.push(routesConfig.pulse.landing.path)}
                score={pulseScore}
              />
              <StyledGrid
                item
                onClick={() => openStats()}
                style={{ cursor: "pointer" }}
              >
                <StyledGrid container alignItems="center">
                  <img
                    src={coinIcon}
                    style={{ marginRight: spacingDefaults.xsmall }}
                  />
                  <StyledCoin
                    clickable={false}
                    color="primary"
                    cursorPointer
                    label={`${userCoinAmount} coins`}
                    size="small"
                  />
                </StyledGrid>
              </StyledGrid>
            </StyledGrid>
          </StyledGrid>
        </StyledGrid>
      )}
    </>
  );
};

export default SubHeader;
